import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import embed from 'vega-embed';
import { getChartColorScale } from '../utils/colorUtils';
import { ChartBarIcon } from '@heroicons/react/24/outline';

interface Problem {
  id: string;
  title: string;
  description: string;
  impact_area: string;
  priority: number;
  stakeholders?: Array<{ name: string; role: string }>;
  metrics?: Array<{
    name: string;
    description: string;
    current_value?: number;
    target_value?: number;
    unit?: string;
  }>;
}

interface Hypothesis {
  id: string;
  title: string;
  hypothesis_statement: string;
  business_impact: number;
  technical_feasibility: number;
  strategy_alignment: number;
  implementation_effort: number;
  priority_score: number;
  problem_id: string;
  ai_capability: string;
  expected_outcome: string;
}

interface ProblemIdentifier {
  id: string;
  number: string;
  symbol: string;
  borderColor: string;
  name: string;
}

interface HypothesisReference {
  id: string;
  problemRef: string;
  hypothesisNum: number;
  fullRef: string;
  symbol: string;
  borderColor: string;
}

export interface MatrixVisualizationHandle {
  exportToSVG: () => Promise<string>;
}

interface MatrixVisualizationProps {
  hypotheses: Hypothesis[];
  problems: Problem[];
  
  // Display options
  showReferences?: boolean;        // Show P1.1 numbering system
  showQuadrantLabels?: boolean;    // Show labeled quadrants
  showLegend?: boolean;            // Show reference system legend
  focusProblemId?: string;         // Grey out other problems
  
  // Size and layout
  size?: 'small' | 'medium' | 'large';
  width?: number | 'container';
  height?: number;
  
  // Axis configuration
  xAxis?: string;
  yAxis?: string;
  
  // Styling
  circleSize?: number;
  fontSize?: number;
  
  // Title
  title?: string;
}

const PROBLEM_SYMBOLS = ['●', '■', '▲', '♦', '+', '◆', '▼', '◇', '⬟', '⬢'];
const PROBLEM_BORDER_COLORS = [
  'rgba(99, 102, 241, 0.6)',   // Soft Indigo
  'rgba(16, 185, 129, 0.6)',   // Soft Emerald
  'rgba(245, 101, 101, 0.6)',  // Soft Red
  'rgba(251, 146, 60, 0.6)',   // Soft Orange
  'rgba(139, 92, 246, 0.6)',   // Soft Purple
  'rgba(236, 72, 153, 0.6)',   // Soft Pink
  'rgba(6, 182, 212, 0.6)',    // Soft Cyan
  'rgba(34, 197, 94, 0.6)',    // Soft Green
  'rgba(168, 85, 247, 0.6)',   // Soft Violet
  'rgba(59, 130, 246, 0.6)'    // Soft Blue
];

const MatrixVisualization = forwardRef<MatrixVisualizationHandle, MatrixVisualizationProps>(({
  hypotheses,
  problems,
  showReferences = false,
  showQuadrantLabels = false,
  showLegend = false,
  focusProblemId,
  size = 'medium',
  width = 'container',
  height,
  xAxis = 'implementation_effort',
  yAxis = 'business_impact',
  circleSize,
  fontSize,
  title
}, ref) => {
  const matrixRef = useRef<HTMLDivElement>(null);
  const viewInstanceRef = useRef<any>(null);

  // Expose export method to parent component
  useImperativeHandle(ref, () => ({
    exportToSVG: async () => {
      if (viewInstanceRef.current) {
        await viewInstanceRef.current.runAsync();
        return viewInstanceRef.current.toSVG();
      }
      throw new Error('Chart not yet rendered');
    }
  }));

  // Size configurations
  const sizeConfig = {
    small: { width: 400, height: 300, circleSize: 400, fontSize: 10 },
    medium: { width: 600, height: 400, circleSize: 600, fontSize: 12 },
    large: { width: 800, height: 500, circleSize: 800, fontSize: 14 }
  };

  const config = sizeConfig[size];
  const finalWidth = width === 'container' ? 'container' : (width || config.width);
  const finalHeight = height || config.height;
  const finalCircleSize = circleSize || config.circleSize;
  const finalFontSize = fontSize || config.fontSize;

  // Generate problem identifiers (only if using references)
  const problemIdentifiers: ProblemIdentifier[] = showReferences 
    ? problems
        .sort((a, b) => (b.priority || 0) - (a.priority || 0))
        .map((problem, index) => ({
          id: problem.id,
          number: `P${index + 1}`,
          symbol: PROBLEM_SYMBOLS[index % PROBLEM_SYMBOLS.length] || '●',
          borderColor: PROBLEM_BORDER_COLORS[index % PROBLEM_BORDER_COLORS.length] || 'rgba(99, 102, 241, 0.6)',
          name: problem.title
        }))
    : [];

  // Generate hypothesis references (only if using references)
  const hypothesisReferences: HypothesisReference[] = [];
  if (showReferences) {
    problemIdentifiers.forEach((problemId) => {
      const problemHypotheses = hypotheses
        .filter(h => h.problem_id === problemId.id)
        .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
      
      problemHypotheses.forEach((hypothesis, index) => {
        hypothesisReferences.push({
          id: hypothesis.id,
          problemRef: problemId.number,
          hypothesisNum: index + 1,
          fullRef: `${problemId.number}.${index + 1}`,
          symbol: problemId.symbol,
          borderColor: problemId.borderColor
        });
      });
    });
  }

  // Get axis labels
  const getAxisLabel = (axis: string) => {
    const labels: Record<string, string> = {
      business_impact: 'Business Impact',
      strategy_alignment: 'Strategic Alignment',
      implementation_effort: 'Implementation Effort (lower is better)',
      technical_feasibility: 'Technical Feasibility'
    };
    return labels[axis] || axis;
  };

  useEffect(() => {
    if (!matrixRef.current || hypotheses.length === 0) return;

    const renderMatrix = async () => {
      try {
        // Prepare data for matrix
        const matrixData = hypotheses.map((h, _index) => {
          const hypRef = showReferences ? hypothesisReferences.find(hr => hr.id === h.id) : null;
          const isGreyedOut = focusProblemId && h.problem_id !== focusProblemId;
          
          // Create deterministic jitter for consistent positioning
          const jitterSeed = h.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
          const jitterX = ((jitterSeed % 100) / 100 - 0.5) * 0.8;
          const jitterY = (((jitterSeed * 7) % 100) / 100 - 0.5) * 0.8;
          
          return {
            id: h.id,
            title: h.title,
            x_value: h[xAxis as keyof Hypothesis] as number,
            y_value: h[yAxis as keyof Hypothesis] as number,
            x_jittered: (h[xAxis as keyof Hypothesis] as number) + jitterX,
            y_jittered: (h[yAxis as keyof Hypothesis] as number) + jitterY,
            priority_score: h.priority_score || 0,
            reference: hypRef?.fullRef || '',
            shortRef: hypRef?.fullRef?.replace('P', '') || '',
            problem: problems.find(p => p.id === h.problem_id)?.title || 'Unknown',
            isGreyedOut: isGreyedOut,
            opacity: isGreyedOut ? 0.3 : 1.0
          };
        });

        // Generate quadrant text if enabled
        const quadrantData = [];
        if (showQuadrantLabels) {
          const midPoint = 5.5;
          const xTextPosition = [2.75, 8.25, 2.75, 8.25];
          const yTextPosition = [8.25, 8.25, 2.75, 2.75];
          
          const metricToVerb = (metric: string) => metric.split('_')[1] || metric;
          const highOrLow = (num: number) => num < midPoint ? "Low" : "High";
          
          quadrantData.push(...xTextPosition.map((x, i) => {
            const y = yTextPosition[i] || 0;
            return {
              x,
              y,
              text: `${highOrLow(y)} ${metricToVerb(yAxis)}, ${highOrLow(x)} ${metricToVerb(xAxis)}`
            };
          }));
        }

        // Determine axis domains
        const xDomain = xAxis === 'implementation_effort' ? [11, 0] : [0, 11];
        const yDomain = yAxis === 'implementation_effort' ? [11, 0] : [0, 11];

        const spec = {
          $schema: "https://vega.github.io/schema/vega-lite/v6.json",
          width: finalWidth,
          height: finalHeight,
          title: title ? {
            text: title,
            fontSize: 16,
            fontWeight: "bold"
          } : undefined,
          layer: [
            // Main scatter plot
            {
              data: { values: matrixData },
              mark: { 
                type: "circle", 
                size: finalCircleSize,
                stroke: "white",
                strokeWidth: 3
              },
              encoding: {
                x: {
                  field: "x_jittered",
                  type: "quantitative",
                  title: getAxisLabel(xAxis),
                  scale: { domain: xDomain }
                },
                y: {
                  field: "y_jittered",
                  type: "quantitative", 
                  title: getAxisLabel(yAxis),
                  scale: { domain: yDomain }
                },
                color: {
                  field: "priority_score",
                  type: "quantitative",
                  scale: getChartColorScale(),
                  legend: { title: "Priority Score" }
                },
                opacity: {
                  field: "opacity",
                  type: "quantitative",
                  scale: { range: [0.3, 1.0] },
                  legend: null
                },
                tooltip: [
                  ...(showReferences ? [{ field: "reference", type: "nominal", title: "Reference" }] : []),
                  { field: "title", type: "nominal", title: "Hypothesis" },
                  { field: "problem", type: "nominal", title: "Problem" },
                  { field: "priority_score", type: "quantitative", format: ".1f", title: "Priority Score" },
                  { field: "y_value", type: "quantitative", title: getAxisLabel(yAxis) },
                  { field: "x_value", type: "quantitative", title: getAxisLabel(xAxis) }
                ]
              }
            },
            
            // Reference labels (if enabled)
            ...(showReferences ? [{
              data: { values: matrixData },
              mark: { 
                type: "text", 
                align: "center", 
                baseline: "middle",
                fontSize: finalFontSize,
                fontWeight: "bold",
                color: "white"
              },
              encoding: {
                x: {
                  field: "x_jittered",
                  type: "quantitative",
                  scale: { domain: xDomain }
                },
                y: {
                  field: "y_jittered",
                  type: "quantitative",
                  scale: { domain: yDomain }
                },
                text: {
                  field: "shortRef",
                  type: "nominal"
                },
                opacity: {
                  field: "opacity",
                  type: "quantitative",
                  scale: { range: [0.3, 1.0] },
                  legend: null
                }
              }
            }] : []),

            // Midpoint lines
            {
              data: { values: [{ x: 5.5 }] },
              mark: { type: "rule", color: "gray", strokeDash: [3, 3] },
              encoding: { x: { field: "x", type: "quantitative", scale: { domain: xDomain } } }
            },
            {
              data: { values: [{ y: 5.5 }] },
              mark: { type: "rule", color: "gray", strokeDash: [3, 3] },
              encoding: { y: { field: "y", type: "quantitative", scale: { domain: yDomain } } }
            },

            // Quadrant labels (if enabled)
            ...(showQuadrantLabels ? [{
              data: { values: quadrantData },
              mark: { 
                type: "text", 
                align: "center", 
                baseline: "middle", 
                fontSize: 10,
                fontStyle: "italic",
                color: "gray"
              },
              encoding: {
                x: { field: "x", type: "quantitative", scale: { domain: xDomain } },
                y: { field: "y", type: "quantitative", scale: { domain: yDomain } },
                text: { field: "text", type: "nominal" }
              }
            }] : [])
          ]
        };

        // Clear and render
        if (matrixRef.current) {
          matrixRef.current.innerHTML = '';
          const result = await embed(matrixRef.current, spec, {
            actions: false,
            renderer: 'svg'
          });
          // Store the view instance for export
          viewInstanceRef.current = result.view;
        }
      } catch (error) {
        console.error('Error rendering matrix:', error);
      }
    };

    renderMatrix();
  }, [hypotheses, problems, showReferences, showQuadrantLabels, focusProblemId, xAxis, yAxis, finalWidth, finalHeight, finalCircleSize, finalFontSize, title, hypothesisReferences, problemIdentifiers]);

  if (hypotheses.length === 0) {
    return (
      <div className="p-6 bg-white rounded-lg border shadow-sm">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <ChartBarIcon className="h-6 w-6 mr-2 text-indigo-600" />
          {title || 'AI Opportunity Prioritization Matrix'}
        </h2>
        <div className="text-center py-12 text-gray-500">
          <ChartBarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No data available for matrix visualization</p>
          <p>Add hypotheses with scoring to see the prioritization matrix.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg border shadow-sm">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <ChartBarIcon className="h-6 w-6 mr-2 text-indigo-600" />
        {title || 'AI Opportunity Prioritization Matrix'}
      </h2>
      
      <div className="space-y-6">
        <div className="flex justify-center">
          <div ref={matrixRef} className="w-full max-w-6xl" />
        </div>
        
        {/* Legend (if enabled and using references) */}
        {showLegend && showReferences && problemIdentifiers.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Reference System</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Problem Mapping</h5>
                <div className="space-y-1">
                  {problemIdentifiers
                    .filter(problemId => hypotheses.some(h => h.problem_id === problemId.id))
                    .map((problemId) => (
                      <div key={problemId.id} className="flex items-center space-x-2 text-sm">
                        <span className="font-semibold">{problemId.number}</span>
                        <span className="text-gray-600">{problemId.name}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">How to Read</h5>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><strong>Reference format:</strong> Problem.Hypothesis (e.g., 1.1, 2.3)</p>
                  <p><strong>Colours:</strong> Represent priority scores (green = high, purple = low)</p>
                  <p><strong>Position:</strong> X = {getAxisLabel(xAxis)}, Y = {getAxisLabel(yAxis)}</p>
                  {focusProblemId && <p><strong>Grayed circles:</strong> Hypotheses from other problems</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

MatrixVisualization.displayName = 'MatrixVisualization';

export default MatrixVisualization;